import type { ECharts } from 'echarts';

export interface KeyboardCallbacks {
  /** A navigation key moved the focus cursor. */
  onFocusChange?: (seriesIndex: number, dataIndex: number) => void;
  /** Enter/Space on the focused date (only fired when drillDownEnabled). */
  onActivate?: (seriesIndex: number, dataIndex: number) => void;
  /** Backspace (only fired when rollUpEnabled). */
  onRollUp?: () => void;
  /** Escape cleared the focus cursor. */
  onEscape?: () => void;
}

/**
 * Keyboard navigation for an ECharts category chart, mirroring the mouse
 * interactions handled by EChartsDragHandler. Attaches a single keydown
 * listener to the focusable chart container and tracks a virtual focus
 * cursor (focused series + focused category/data index).
 */
export class EChartsKeyboardHandler {
  private chart: ECharts;
  private container: HTMLElement;
  private callbacks: KeyboardCallbacks;
  private boundKeydown: (e: KeyboardEvent) => void;

  /** Enable Enter/Space -> drilldown. Set by the host on each chart update. */
  public drillDownEnabled = false;
  /** Enable Backspace -> rollup. Set by the host on each chart update. */
  public rollUpEnabled = false;

  private _focusedSeries = 0;
  private _focusedDataIndex = -1;

  constructor(chart: ECharts, container: HTMLElement, callbacks: KeyboardCallbacks) {
    this.chart = chart;
    this.container = container;
    this.callbacks = callbacks;
    this.boundKeydown = this.onKeydown.bind(this);
    this.container.addEventListener('keydown', this.boundKeydown);
  }

  get focusedDataIndex(): number {
    return this._focusedDataIndex;
  }

  get focusedSeries(): number {
    return this._focusedSeries;
  }

  reset(): void {
    this._focusedDataIndex = -1;
    this._focusedSeries = 0;
  }

  destroy(): void {
    this.container?.removeEventListener('keydown', this.boundKeydown);
  }

  private getCategoryCount(option: any): number {
    return option?.xAxis?.[0]?.data?.length ?? 0;
  }

  private getSeriesCount(option: any): number {
    return option?.series?.length ?? 0;
  }

  private onKeydown(e: KeyboardEvent): void {
    const option = this.chart.getOption() as any;
    const categoryCount = this.getCategoryCount(option);
    if (categoryCount === 0) return;

    // On the very first navigation key press (cursor is unfocused), initialize
    // the data cursor to index 0 before applying the key's own logic.
    const isNavKey =
      e.key === 'ArrowLeft' || e.key === 'ArrowRight' ||
      e.key === 'ArrowUp'   || e.key === 'ArrowDown'  ||
      e.key === 'Home'      || e.key === 'End';
    let justInitialized = false;
    if (this._focusedDataIndex < 0 && isNavKey) {
      this._focusedDataIndex = 0;
      justInitialized = true;
    }

    switch (e.key) {
      case 'ArrowRight':
        // Do not advance past 0 on the press that just initialized the cursor.
        if (!justInitialized) {
          this._focusedDataIndex = Math.min(this._focusedDataIndex + 1, categoryCount - 1);
        }
        break;
      case 'ArrowLeft':
        this._focusedDataIndex = Math.max(this._focusedDataIndex - 1, 0);
        break;
      case 'Home':
        this._focusedDataIndex = 0;
        break;
      case 'End':
        this._focusedDataIndex = categoryCount - 1;
        break;
      case 'ArrowDown': {
        const count = this.getSeriesCount(option);
        if (count > 0) this._focusedSeries = (this._focusedSeries + 1) % count;
        break;
      }
      case 'ArrowUp': {
        const count = this.getSeriesCount(option);
        if (count > 0) this._focusedSeries = (this._focusedSeries - 1 + count) % count;
        break;
      }
      case 'Enter':
      case ' ':
        if (this.drillDownEnabled && this._focusedDataIndex >= 0) {
          e.preventDefault();
          this.callbacks.onActivate?.(this._focusedSeries, this._focusedDataIndex);
        }
        return;
      case 'Backspace':
        if (this.rollUpEnabled) {
          e.preventDefault();
          this.callbacks.onRollUp?.();
        }
        return;
      case 'Escape':
        this._focusedDataIndex = -1;
        this.callbacks.onEscape?.();
        return;
      default:
        return; // ignore unrelated keys, don't preventDefault
    }

    e.preventDefault();
    // Clamp the focused series in case the series set shrank.
    const seriesCount = this.getSeriesCount(option);
    if (this._focusedSeries >= seriesCount) {
      this._focusedSeries = Math.max(0, seriesCount - 1);
    }
    this.callbacks.onFocusChange?.(this._focusedSeries, this._focusedDataIndex);
  }
}
